import React from 'react'
import {
  Admin,
  Resource,
  CustomRoutes,
  localStorageStore,
  StoreContextProvider,
} from 'react-admin'
import { Route } from 'react-router-dom'
import AdminInvitation from './pages/AdminInvitation'
import ResetPassword from './pages/ResetPassword'

import { Dashboard } from './Dashboard'
import { Login } from './Login'

import {
  ServiceConfigShow,
  ServiceConfigList,
  ServiceConfigEdit,
} from './resources/serviceConfigs'

import { UserList, UserShow, UserInvite } from './resources/users'
import { InterceptList, InterceptShow } from './resources/intercepts'
import { PurchaseList, PurchaseShow } from './resources/purchases'

// https://mui.com/material-ui/material-icons/?theme=Outlined&query=report
import ServiceConfigIcon from '@mui/icons-material/MiscellaneousServices'
import UserIcon from '@mui/icons-material/Person'
import InterceptIcon from '@mui/icons-material/Lock'
import PurchaseIcon from '@mui/icons-material/ShoppingCart'

import { authProvider, buildResourcePermissions } from './authProvider'
import { dataProvider } from './dataProvider'

import MyLayout from './layout/Layout'
import { ProfileEdit } from './pages/profile'
import { customDarkTheme, customLightTheme, logInBackgroundImage } from './theme/myCustomTheme'

const resources = [
  <Resource
    key="serviceConfigs"
    name="serviceConfigs"
    list={ServiceConfigList}
    edit={ServiceConfigEdit}
    show={ServiceConfigShow}
    recordRepresentation="title"
    icon={ServiceConfigIcon}
    options={{ label: 'ServiceConfigs' }}
  />,
  <Resource
    name="users"
    create={UserInvite}
    list={UserList}
    show={UserShow}
    recordRepresentation="email"
    icon={UserIcon}
    options={{ label: 'Users' }}
  />,
  <Resource
    name="intercepts"
    list={InterceptList}
    show={InterceptShow}
    recordRepresentation="code"
    icon={InterceptIcon}
    options={{ label: 'Intercepts' }}
  />,
  <Resource
    name="purchases"
    list={PurchaseList}
    show={PurchaseShow}
    recordRepresentation="userId"
    icon={PurchaseIcon}
    options={{ label: 'Purchases' }}
  />,
]

const filterResources = () => {
  const resourcesMap = buildResourcePermissions()
  const resourceNames = Object.keys(resourcesMap)

  return resources
    .filter((r) => resourceNames.includes(r.props.name))
    .map((resource) => {
      const resourceName = resource.props.name
      const permissions = resourcesMap[resourceName]

      const newProps = {
        name: resource.props.name,
        recordRepresentation: resource.props.recordRepresentation,
        icon: resource.props.icon,
        options: resource.props.options,
      }

      if (permissions.isRead) {
        if (resource.props.list) {
          newProps.list =
            typeof resource.props.list === 'function'
              ? resource.props.list({
                  isEdit: permissions.isEdit || false,
                  isDelete: permissions.isDelete || false,
                  isCreate: permissions.isCreate || false,
                })
              : resource.props.list
        } else {
          newProps.list = undefined
        }
        if (resource.props.show) {
          newProps.show = resource.props.show
        } else {
          newProps.show = undefined
        }
      } else {
        newProps.list = undefined
        newProps.show = undefined
      }

      if (permissions.isEdit && resource.props.edit) {
        newProps.edit = resource.props.edit
      } else {
        newProps.edit = undefined
      }

      if (permissions.isCreate && resource.props.create) {
        newProps.create = resource.props.create
      } else {
        newProps.create = undefined
      }

      return React.cloneElement(resource, newProps)
    })
}

const store = localStorageStore(undefined, 'cipher-street-admin')

const App = () => (
  <Admin
    loginPage={
      <Login
        style={{
          backgroundImage: logInBackgroundImage,
        }}
      />
    }
    dataProvider={dataProvider}
    authProvider={authProvider}
    store={store}
    dashboard={Dashboard}
    layout={MyLayout}
    theme={customLightTheme}
    darkTheme={customDarkTheme}
    defaultTheme="light"
  >
    <CustomRoutes>
      <Route key="my-profile" path="/my-profile" element={<ProfileEdit />} />
    </CustomRoutes>
    {filterResources}

    <CustomRoutes noLayout disableAuthentication>
      <Route key="admin-invitation" path="/admin-invitation" element={<AdminInvitation />} />
      <Route key="reset-password" path="/reset-password" element={<ResetPassword />} />
    </CustomRoutes>
  </Admin>
)

const AppWrapper = () => (
  <StoreContextProvider value={store}>
    <App />
  </StoreContextProvider>
)

export default AppWrapper
